import { Button } from '@ctw/shared/components/button';
import type {
  Filter,
  FilterChangeEvent,
  FilterItem,
  FilterOptionSelect,
} from '@ctw/shared/components/filter-bar/filter-bar-types';
import { SortButton } from '@ctw/shared/components/sort-button/sort-button';
import { useFiltersModal } from '@ctw/shared/components/view-controls/filters-modal';
import { PromotedFilterDropdown } from '@ctw/shared/components/view-controls/promoted-filter-dropdown';
import { partitionFilters } from '@ctw/shared/components/view-controls/promoted-filters';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faSliders } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';

export interface ViewControlsToolbarSortProps {
  className?: ClassName;
  defaultSort: string;
  options: Array<string>;
  onChange: (sort: string) => void;
}

// A menu filter counts toward the "Filters (N)" badge only when it holds a real
// constraint — not merely a default value present in the selection. A checkbox with
// no chosen values, a tag that's off, or a select sitting at its first (default)
// option are all "inactive".
const isFilterActive = (filter: FilterItem, value: Filter | undefined): boolean => {
  if (!value) {
    return false;
  }
  switch (value.type) {
    case 'checkbox':
      return Array.isArray(value.selected) && value.selected.length > 0;
    case 'tag':
      return value.selected === true;
    case 'select': {
      const firstValue = filter.type === 'select' ? filter.values[0] : undefined;
      const firstKey = typeof firstValue === 'string' ? firstValue : firstValue?.key;
      return value.selected !== undefined && value.selected !== firstKey;
    }
    default:
      return false;
  }
};

export interface ViewControlsToolbarSelectMenu {
  key: string;
  label: string;
  options: Array<{ key: string; name: string }>;
  selectedKey: string;
  onChange: (optionKey: string) => void;
}

export interface ViewControlsToolbarProps {
  id?: string;
  testId?: string;
  className?: ClassName;
  filters: Array<FilterItem>;
  selected: FilterChangeEvent;
  onFilterChange: (filters: FilterChangeEvent) => void;
  sort?: ViewControlsToolbarSortProps;
  selectMenus?: Array<ViewControlsToolbarSelectMenu>;
  /** Reserved region for the future view-mode selector. */
  trailing?: ReactNode;
}

export const ViewControlsToolbar = ({
  id,
  testId,
  className,
  filters,
  selected,
  onFilterChange,
  sort,
  selectMenus,
  trailing,
}: ViewControlsToolbarProps) => {
  const { trackInteraction } = useTelemetry();

  const { promoted, menu, optionGroups, toggles } = partitionFilters(filters);
  const menuKeys = new Set(menu.map((filter) => filter.key));
  const menuSelected = Object.fromEntries(
    Object.entries(selected).filter(([key]) => menuKeys.has(key)),
  );
  const openFiltersModal = useFiltersModal({
    filters: menu,
    selected: menuSelected,
    onUpdate: (updatedMenuSelected) =>
      onFilterChange({
        ...Object.fromEntries(Object.entries(selected).filter(([key]) => !menuKeys.has(key))),
        ...updatedMenuSelected,
      }),
  });
  const activeMenuCount = menu.filter((filter) =>
    isFilterActive(filter, selected[filter.key]),
  ).length;
  // Only offer the Filters button when the modal would have something to show — i.e. there is at
  // least one option group or toggle. Promoted-only (or no) filters leave nothing to open.
  const hasMenuFilters = optionGroups.length > 0 || toggles.length > 0;

  const promotedSelectedValue = (filter: FilterOptionSelect) => {
    const current = selected[filter.key];
    if (current?.type === 'select') {
      return current.selected;
    }
    const firstValue = filter.values[0];
    return typeof firstValue === 'string' ? firstValue : (firstValue?.key ?? '');
  };

  const onPromotedChange = (filter: FilterOptionSelect, valueKey: string) => {
    if (filter.key === 'range') {
      trackInteraction('range_change', { range: valueKey });
    }
    onFilterChange({
      ...selected,
      [filter.key]: { key: filter.key, type: 'select', selected: valueKey },
    });
  };

  return (
    <div
      id={id}
      data-testid={testId}
      className={twx(className, 'flex w-full flex-wrap items-center gap-2')}
    >
      <div className={tw`flex flex-wrap items-center gap-2`}>
        {promoted
          .filter((filter): filter is FilterOptionSelect => filter.type === 'select')
          .map((filter) => (
            <PromotedFilterDropdown
              key={filter.key}
              filter={filter}
              selectedValue={promotedSelectedValue(filter)}
              onChange={(valueKey) => onPromotedChange(filter, valueKey)}
            />
          ))}
        {sort && (
          <SortButton
            className={twx(sort.className)}
            defaultSort={sort.defaultSort}
            options={sort.options}
            onChange={sort.onChange}
          />
        )}
        {selectMenus?.map((selectMenu) => (
          <PromotedFilterDropdown
            key={selectMenu.key}
            filter={{
              key: selectMenu.key,
              display: selectMenu.label,
              type: 'select',
              promoted: true,
              values: selectMenu.options,
            }}
            selectedValue={selectMenu.selectedKey}
            onChange={selectMenu.onChange}
          />
        ))}
      </div>

      <div className={tw`ml-auto flex items-center gap-2`}>
        {hasMenuFilters && (
          <Button
            type="button"
            variant="unstyled"
            onClick={() => openFiltersModal()}
            className={tw`flex cursor-pointer items-center gap-2 rounded-md px-3 py-1 font-medium text-content-subtle text-sm hover:bg-background-hover`}
          >
            <FontAwesomeIcon icon={faSliders} className={tw`w-4`} />
            Filters ({activeMenuCount})
          </Button>
        )}
        {trailing}
      </div>
    </div>
  );
};
